import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Icon, IconProp } from '@fortawesome/fontawesome-svg-core';
import { Language, User } from '../models/core.models';
import { Observable, firstValueFrom, from, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AuthTokenResponse } from './user.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface LoginCredentials {
  email: string;
  password: string;
  facebook_user_id?: string;
  facebook_access_token?: string;
  remember_me?: boolean;
}
export interface ErrorResponse {
  errors: Error;
  message: string;
  status_code: number;
}
export interface LoginResponse {
  access_token: string;
  access_token_expires_at: string | number;
  user: User;
}

export interface LoginErrorResponse {
  error: true;
  code: string | number;
  message: string;
}

export class LoginApiError extends Error {
  constructor(
    public readonly code: string | number,
    message: string,
    public readonly httpStatus?: number,
  ) {
    super(message);
    this.name = 'LoginApiError';
  }
}

export interface Credentials {
  token: string;
  /** Access token expiry as unix seconds — drives proactive renewal */
  expiresAt?: number;
}

export interface UserSession {
  session_id: number | string;
  user_id: string;
  company_id: string;
  ip_address: string;

  // Mapping the new transformer fields
  browser_raw: string; // The full User Agent string
  browser_brand:
    | 'Chrome'
    | 'Firefox'
    | 'Safari'
    | 'Edge'
    | 'Opera'
    | 'Internet Explorer'
    | 'Other';

  resolution?: string;
  browser_icon?: IconProp;
  is_current: boolean;
  is_impersonating: boolean;

  last_active: number; // Timestamp (seconds)
  started_at: number; // Timestamp (seconds)
  expires_at?: number; // Timestamp (seconds)

  user?: {
    name: string;
    email: string;
    avatar_url?: string;
  };

  company?: {
    id?: string;
    name: string;
  };
}

export interface ActiveSessionsResponse {
  sessions?: UserSession[]; // For personal list
  active_sessions?: UserSession[]; // For global list
}

const credentialsKey = 'sensika-auth';
const rememberKey = 'sensika-remember-me';
/** Web Locks name serializing refresh-token rotation across tabs */
const refreshLockKey = 'sensika-token-refresh';
/** Renew the access token this many seconds before it expires */
const proactiveRefreshMarginSeconds = 300;

/** Backend sends unix seconds; tolerate ms or numeric strings */
function normalizeEpochSeconds(
  value: string | number | undefined,
): number | undefined {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n > 1e12 ? Math.floor(n / 1000) : n;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private credentials: Credentials | null;

  private language: Language;

  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
  ) {
    this.credentials = this.readStoredCredentials();
    this.scheduleProactiveRefresh();

    // Keep the in-memory copy in sync when another tab rotates the token
    // (fires only for localStorage changes, i.e. remember-me mode)
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === credentialsKey) {
        this.credentials = this.readStoredCredentials();
        this.scheduleProactiveRefresh();
      }
    });
  }

  private readStoredCredentials(): Credentials | null {
    try {
      return JSON.parse(
        sessionStorage.getItem(credentialsKey) ||
          localStorage.getItem(credentialsKey) ||
          'null',
      );
    } catch {
      return null;
    }
  }

  setLanguage(language: Language) {
    this.language = language;
  }

  getLanguage(): Language {
    return this.language;
  }

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    const payload = {
      email: credentials.email,
      password: credentials.password,
      browser: navigator.userAgent,
      resolution: `${window.screen.width}x${window.screen.height}`,
      ...(credentials.facebook_user_id
        ? { facebook_user_id: credentials.facebook_user_id }
        : {}),
      ...(credentials.facebook_access_token
        ? { facebook_access_token: credentials.facebook_access_token }
        : {}),
    };

    return this.httpClient.post<LoginResponse>('/auth/login', payload).pipe(
      // Optional runtime sanity check
      map((res) => {
        if (!res?.access_token) {
          throw new Error('Invalid login response structure');
        }
        return res;
      }),
      catchError((e: unknown) => {
        if (e instanceof HttpErrorResponse) {
          const err = e.error as Partial<LoginErrorResponse> | string | null;

          // backend error envelope
          if (err && typeof err === 'object' && err.error === true) {
            return throwError(
              () =>
                new LoginApiError(
                  err.code ?? e.status,
                  err.message ?? 'Login failed',
                  e.status,
                ),
            );
          }

          // fallback
          const msg =
            (typeof err === 'string' && err) ||
            (err &&
              typeof err === 'object' &&
              'message' in err &&
              (err as any).message) ||
            e.message ||
            'Login failed';

          return throwError(() => new LoginApiError(e.status, msg, e.status));
        }

        return throwError(() => e);
      }),
    );
  }

  clearCredentials() {
    this.cancelProactiveRefresh();
    this.credentials = null;
    sessionStorage.removeItem(credentialsKey);
    localStorage.removeItem(credentialsKey);
    // Clear per-tab session context
    sessionStorage.removeItem('sensika-tab-id');
    sessionStorage.removeItem('sensika-tab-company-id');
  }

  logout(): Observable<any> {
    // Revoke the session server-side first (needs the bearer token) and let the
    // backend expire the refresh cookie; local cleanup happens regardless.
    return this.httpClient
      .post('/auth/logout', {}, { withCredentials: true })
      .pipe(
        catchError(() => of(null)), // best-effort — never block logout on the API
        tap(() => {
          this.clearCredentials();
          this.router.navigate(['/auth/login']);
        }),
        map(() => ({ success: true, error: false })),
      );
  }

  verifyAuthToken(token: string): Observable<LoginResponse> {
    return this.httpClient.get<LoginResponse>('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  hasCredentials(): boolean {
    return !!this.credentials && !!this.credentials.token;
  }

  getCredentials(): Credentials | null {
    return this.credentials;
  }

  getRememberMe(): boolean {
    const raw = localStorage.getItem(rememberKey);

    if (raw === null) {
      return false;
    }

    try {
      return JSON.parse(raw) === true;
    } catch {
      // cleanup invalid legacy values like "undefined"
      localStorage.removeItem(rememberKey);
      return false;
    }
  }

  setCredentials(credentials?: Credentials, remember?: boolean) {
    // When remember is not given (e.g. token refresh), keep the current mode —
    // previously a refresh silently moved the token to sessionStorage while the
    // stale one stayed in localStorage.
    const effectiveRemember = remember ?? this.getRememberMe();
    localStorage.setItem(rememberKey, JSON.stringify(effectiveRemember));
    this.credentials = credentials || { token: '' };
    if (credentials) {
      const storage = effectiveRemember ? localStorage : sessionStorage;
      const otherStorage = effectiveRemember ? sessionStorage : localStorage;
      storage.setItem(credentialsKey, JSON.stringify(credentials));
      otherStorage.removeItem(credentialsKey); // no stale copies
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
    }
    this.scheduleProactiveRefresh();
  }

  loginToken(token: string): Observable<AuthTokenResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });

    const urlEncodedData = new HttpParams()
      .set('token', token)
      .set('browser', navigator.userAgent)
      .set('resolution', `${window.screen.width}x${window.screen.height}`);

    return this.httpClient.post<AuthTokenResponse>(
      '/auth/auth_token',
      urlEncodedData.toString(),
      { headers },
    );
  }

  refresh(): Observable<{
    access_token: string;
    access_token_expires_at?: string | number;
  }> {
    // Cookie is sent automatically if withCredentials is true and CORS allows it
    return this.httpClient.post<{
      access_token: string;
      access_token_expires_at?: string | number;
    }>('/auth/refresh', {}, { withCredentials: true });
  }

  /**
   * Rotates the access token via the refresh cookie, serialized across tabs.
   *
   * The backend rotates the refresh token on every call, so two concurrent
   * refreshes (two tabs hitting 401 together) would invalidate each other. A
   * Web Lock serializes them; once inside the lock we re-read storage — if
   * another tab already renewed while we waited, reuse its token instead of
   * rotating again.
   *
   * @param staleToken the token that was rejected (or is about to expire)
   */
  refreshSession(staleToken?: string | null): Observable<string> {
    const doRefresh = async (): Promise<string> => {
      const stored = this.readStoredCredentials();
      if (stored?.token && staleToken && stored.token !== staleToken) {
        this.credentials = stored;
        this.scheduleProactiveRefresh();
        return stored.token;
      }

      const res = await firstValueFrom(this.refresh());
      this.setCredentials({
        token: res.access_token,
        expiresAt: normalizeEpochSeconds(res.access_token_expires_at),
      });
      return res.access_token;
    };

    const locks = (navigator as any)?.locks;
    return from(
      locks?.request
        ? (locks.request(refreshLockKey, doRefresh) as Promise<string>)
        : doRefresh(),
    );
  }

  private cancelProactiveRefresh(): void {
    if (this.proactiveRefreshTimer !== null) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  /**
   * Renews the access token shortly before it expires so users with an open
   * tab never hit a 401 round-trip. Reactive refresh (interceptor) remains the
   * fallback when this misses (laptop asleep, tab throttled, ...).
   */
  private scheduleProactiveRefresh(): void {
    this.cancelProactiveRefresh();

    const expiresAt = this.credentials?.expiresAt;
    if (!this.credentials?.token || !expiresAt) {
      return;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const delaySeconds = Math.max(
      expiresAt - proactiveRefreshMarginSeconds - nowSeconds,
      10,
    );

    this.proactiveRefreshTimer = setTimeout(() => {
      const currentToken = this.credentials?.token;
      this.refreshSession(currentToken).subscribe({
        // Errors are left to the reactive 401 flow (and logout on dead session)
        error: () => undefined,
      });
    }, delaySeconds * 1000);
  }

  /**
   * Fetches ALL active sessions in the system (GET /auth/sessions is the
   * global, admin-restricted listing on the backend — there is currently no
   * personal-sessions endpoint).
   */
  getActiveSessions(): Observable<UserSession[]> {
    return this.httpClient.get<UserSession[]>('/auth/sessions');
  }

  /**
   * Fetches ALL active sessions in the system (Admin only).
   */
  getGlobalActiveSessions(): Observable<UserSession[]> {
    return this.httpClient
      .get<ActiveSessionsResponse>('/auth/active-sessions')
      .pipe(map((res) => res.active_sessions || []));
  }

  /**
   * Revokes a specific session by ID.
   * Works for both personal sessions and admin-forced terminations.
   * * @param sessionId The ID of the session to terminate
   * @param isAdmin Whether to use the admin endpoint path
   */
  revokeSession(sessionId: string | number): Observable<any> {
    const endpoint = `/auth/sessions`;

    // We construct the body as a URL-encoded string so PHP's parse_str can read it
    const body = new HttpParams()
      .set('session_id', sessionId.toString()) // Using camelCase as your backend transforms keys
      .toString();

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.httpClient.delete(endpoint, {
      headers: headers,
      body: body,
    });
  }
}
